import { useCallback, useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { notificationHref } from "./deepLink";
import { NotificationsDropdown, type NotificationsDropdownLabels } from "./NotificationsDropdown";
import { useNotifications, type UseNotificationsResult } from "./useNotifications";
import type { AppNotification } from "./types";

/** The dropdown props that come straight from the feed. */
export function feedDropdownProps(feed: UseNotificationsResult) {
  return {
    items: feed.items,
    unread: feed.unread,
    loadState: feed.loadState,
    connection: feed.connection,
    hasMore: feed.hasMore,
    loadingMore: feed.loadingMore,
    onMarkAllRead: feed.markAllRead,
    onLoadMore: feed.loadMore,
    onRetry: feed.retry,
  };
}

/** Click on a notification: mark it read, then open what it is about (§5). */
export function useOpenNotification(markRead: (id: string) => void) {
  const navigate = useNavigate();
  return useCallback(
    (item: AppNotification) => {
      if (!item.read_at) markRead(item.id);
      navigate(notificationHref(item));
    },
    [markRead, navigate],
  );
}

/**
 * Notification bell (design: `weco2/shell.jsx` Header). The dot is real: it
 * shows while the service reports unread notifications, and clears when they
 * are read here, on another tab or on the phone. The mock's permanent unread
 * dot is deliberately not reproduced.
 */
export function NotificationsBell({
  label,
  labels,
}: {
  label: string;
  labels?: NotificationsDropdownLabels;
}) {
  const location = useLocation();
  const feed = useNotifications();
  const openItem = useOpenNotification(feed.markRead);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        title={label}
        onClick={() => setOpen((cur) => !cur)}
        className="relative w-9 h-9 rounded-[14px] flex items-center justify-center text-foreground/70 hover:bg-foreground/5 hover:text-foreground active:scale-95 transition-all"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Bell size={18} strokeWidth={2} aria-hidden />
        {feed.unread > 0 && (
          <span
            data-testid="notifications-unread-dot"
            className="absolute top-2 end-2 w-2 h-2 bg-primary rounded-full ring-2 ring-background"
          />
        )}
      </button>
      <NotificationsDropdown
        open={open}
        anchorRef={ref}
        labels={{ ...labels, title: label }}
        {...feedDropdownProps(feed)}
        onSelect={(item) => {
          setOpen(false);
          openItem(item);
        }}
        onClose={close}
      />
    </div>
  );
}
